import { now } from '@/shared/utils/dateUtils';
interface StorageInfo {
  key: string;
  size: number;
  lastAccessed?: number;
}

const STORAGE_SIZE_LIMIT = 5 * 1024 * 1024; // 5MB limit

export class StoreCleanupManager {
  private static readonly LAST_CLEANUP_KEY = 'bot-unify-last-cleanup';

  /**
   * Performs cleanup of old or oversized localStorage data
   */
  static performCleanup(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;

    try {
      const lastCleanup = localStorage.getItem(this.LAST_CLEANUP_KEY);
      const timestamp = now();

      // Only cleanup once per day
      if (lastCleanup) {
        const lastCleanupTime = parseInt(lastCleanup, 10);
        if (timestamp - lastCleanupTime < 24 * 60 * 60 * 1000) {
          return;
        }
      }

      // Analyze storage (for future use)
      this.analyzeStorage();

      // Clear old data if storage is getting full
      if (this.getTotalStorageSize() > STORAGE_SIZE_LIMIT * 0.8) {
        this.clearOldData();
      }

      // Update last cleanup time
      localStorage.setItem(this.LAST_CLEANUP_KEY, now.toString());
    } catch (error) {
      console.error('Store cleanup error:', error);
    }
  }

  /**
   * Analyzes current localStorage usage
   */
  private static analyzeStorage(): StorageInfo[] {
    const info: StorageInfo[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('bot-unify-')) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            info.push({
              key,
              size: value.length * 2, // UTF-16 characters are 2 bytes each
            });
          }
        } catch (error) {
          console.error(`Error analyzing storage for ${key}:`, error);
        }
      }
    }

    return info;
  }

  /**
   * Gets total size of all store data in localStorage
   */
  private static getTotalStorageSize(): number {
    let totalSize = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('bot-unify-')) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            totalSize += value.length * 2; // UTF-16 characters are 2 bytes each
          }
        } catch (error) {
          console.error(`Error calculating size for ${key}:`, error);
        }
      }
    }

    return totalSize;
  }

  /**
   * Clears old or corrupted data
   */
  private static clearOldData(): void {
    const keys: string[] = [];

    // Collect all keys with the 'bot-unify-' prefix
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        key.startsWith('bot-unify-') &&
        key !== this.LAST_CLEANUP_KEY
      ) {
        keys.push(key);
      }
    }

    keys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        if (!value) return;

        const parsed = JSON.parse(value);

        // Check if data has version info and state
        if (parsed.version !== undefined && parsed.state) {
          // Keep the data if it's valid and recent
          return;
        }

        // Clear invalid or old format data
        localStorage.removeItem(key);
      } catch (error) {
        // If parsing fails, remove the corrupted data
        console.error(`Removing corrupted data for ${key}:`, error);
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Manually trigger cleanup (for debugging)
   */
  static forceCleanup(): void {
    localStorage.removeItem(this.LAST_CLEANUP_KEY);
    this.performCleanup();
  }

  /**
   * Get storage statistics
   */
  static getStorageStats(): {
    totalSize: number;
    stores: StorageInfo[];
    sizeLimit: number;
  } {
    return {
      totalSize: this.getTotalStorageSize(),
      stores: this.analyzeStorage(),
      sizeLimit: STORAGE_SIZE_LIMIT,
    };
  }
}

// Auto-cleanup on load
if (typeof window !== 'undefined') {
  // Defer cleanup to avoid blocking initial render
  setTimeout(() => {
    StoreCleanupManager.performCleanup();
  }, 5000);
}
